import {
  generateImageSourceString,
  imageTypeMetadataMap,
  MimeType,
} from "../imageTypeMetadataMap";

// TODO(herbison): Move this logic to a better location.

/** Base shape for messages sent by Jupyter message emitter. */
interface JupyterOutputAddedRequestBase<T> {
  output: T;
}

interface StringData {
  "text/plain": string;
}

type PossibleImageData = StringData & { [imageType in MimeType]: string };

/**
 * Interface which reflects the shape of the Jupyter response to when
 * code which generates output is executed.
 *
 * The native Jupyter objects used for this response are not great but we have
 * to work with what they offer us.
 */
export type JupyterOutputAddedRequest =
  // https://ipython.org/ipython-doc/3/development/messaging.html#streams-stdout-stderr-etc
  | JupyterOutputAddedRequestBase<{ output_type: "stream"; text: string }>
  // https://ipython.org/ipython-doc/3/development/messaging.html#id4
  | JupyterOutputAddedRequestBase<{
      output_type: "execute_result";
      data: PossibleImageData;
    }>
  // https://ipython.org/ipython-doc/3/development/messaging.html#display-data
  | JupyterOutputAddedRequestBase<{
      output_type: "display_data";
      data: PossibleImageData;
    }>
  // https://ipython.org/ipython-doc/3/development/messaging.html#execution-errors
  | JupyterOutputAddedRequestBase<{
      output_type: "error";
      ename: string;
      evalue: string;
      traceback: string[];
    }>;

type OutputAdded = | {
  type: "message";
  value: string;
}
| {
  type: "images";
  values: string[];
}
| {
  type: "error";
  ename: string;
  evalue: string;
  traceback: string[];
}
| {
  type: "unknown";
};

export const toOutputAdded = (
  req: JupyterOutputAddedRequest
): OutputAdded => {
  switch (req.output.output_type) {
    case "stream":
      return { type: "message", value: req.output.text };
    case "execute_result":
    case "display_data":
      const images: string[] = [];
      const { data } = req.output;
      imageTypeMetadataMap.forEach((metadata, _imageType, _map) => {
        const { mimeType } = metadata;
        const imageData: string = data[mimeType];
        if (imageData) {
          images.push(generateImageSourceString(mimeType, imageData));
        }
      });
      if (!!images.length) {
        return { type: "images", values: images };
      }
      return { type: "message", value: data["text/plain"] };
    case "error":
      return {
        type: "error",
        ename: req.output.ename,
        evalue: req.output.evalue,
        traceback: req.output.traceback,
      };
    default:
      return {
        type: "unknown",
      };
  }
};
